/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { DOCUMENT, isPlatformBrowser, Location } from '@angular/common';
import { computed, effect, inject, Injectable, PLATFORM_ID, Signal, signal } from '@angular/core';
import { isReadable, TinyColor } from '@ctrl/tinycolor';

import { Environment, ENVIRONMENT, MovieSiteCustomNavigationButtonConfig } from '@environment';
import { ChannelService } from '@usheru-hff/sdk/channel';
import {
  MovieSiteLandingConfiguration,
  MovieSiteTopMenuConfiguration,
  MovieSiteColorSchemeConfiguration,
  MovieSiteTheme,
  MovieSiteFontsConfiguration,
  MovieSiteFooterConfiguration,
  MovieSiteLandingVideoConfiguration,
  MovieSiteConfigGlobal,
  MovieSiteFooterLogoParams,
  HorizontalPosition,
  VerticalPosition,
  MovieSiteSynopsisConfiguration,
  LengthUnits,
  MovieSitePageConfiguration,
  MovieSiteLogosConfiguration,
  MovieSiteLandingCoverConfiguration
} from '@usheru-hff/sdk/movie-site';
import {
  getChannelMovieProp,
  Movie,
  MovieProperties,
  MovieSiteConfigProperties,
  MovieTranslations
} from '@usheru-hff/shared/domain';
import { ImageService } from '@usheru-hff/shared/utils-image';
import { MetaTags, MetatagsService } from '@usheru-hff/shared/utils-metatag';
import { TitleService } from '@usheru-hff/shared/utils-title';

import { MOVIE_SITE_OVERRIDE_CSS, BODY_CLASS_NAME } from './movie-site-override-css';
import { generateFontFace } from './utils-font';
import { FooterComponent } from '../footer';
import { MovieSiteSideMenuColorSchemeConfiguration } from './top-menu/movie-site-side-menu-color-scheme.model';

interface Status {
  pageConfiguration?: Partial<MovieSitePageConfiguration>;
  topMenuConfiguration: Partial<MovieSiteTopMenuConfiguration>;
  landingConfiguration: Partial<MovieSiteLandingConfiguration>;
  synopsisConfiguration: Partial<MovieSiteSynopsisConfiguration>;
  footerConfiguration?: Partial<MovieSiteFooterConfiguration>;
  logosConfiguration?: Partial<MovieSiteLogosConfiguration>;
  theme: MovieSiteTheme;
}

const INITIAL_COLOR_SCHEME: Omit<MovieSiteColorSchemeConfiguration, 'generatedValues' | 'extraValues'> = {
  brandColor: '#007bff',
  backgroundColor: '#ffffff',
  borderRadius: '12px'
};

const INITIAL_FONTS: MovieSiteFontsConfiguration = {
  font: undefined, //'Poppins-Regular.ttf',
  fontBold: undefined //'Poppins-ExtraBold.ttf'
};

const INITIAL_STATUS: Status = {
  pageConfiguration: {},
  theme: {
    colorScheme: INITIAL_COLOR_SCHEME,
    fonts: INITIAL_FONTS
  },
  logosConfiguration: {},
  topMenuConfiguration: {},
  landingConfiguration: {
    display: 'image',
    mode: 'cover-screen',
    modeMobile: 'contain',
    gradientBottom: true,
    gradientBottomMobile: 'inherit',
    parallaxEffect: true,
    logoPosition: {
      mainPosition: ['left', 'bottom'],
      offset: [0, 'px', 0, 'px']
    },
    releaseMessage: {
      attachedToLogo: false,
      maxWidth: '400px'
    },
    releaseMessagePosition: {
      horizontal: 'left'
    },
    logo: {
      type: 'movie-logo',
      maxWidth: '400px',
      mobileMaxWidth: '400px'
    },
    mediaFit: 'cover',
    mediaPosition: 'center'
  },
  synopsisConfiguration: {
    backgroundColor: null,
    headerText: null,
    bodyTextColor: null,
    textAlign: 'justify',
    textAlignSM: 'justify'
  }
};

const DEFAULT_FOOTER_COMPONENT = FooterComponent;

const INITIAL_LANDING_VIDEO: Partial<MovieSiteLandingConfiguration> = {
  display: 'video',
  mode: 'cover-screen',
  modeMobile: 'cover-screen',
  logoPosition: {
    mainPosition: ['center', 'center'],
    offset: [0, 'px', 0, 'px']
  },
  gradientIntensity: '0.5,1,33%'
};

/**
 * Valuer for the generatedValues in the color scheme.
 */
const THEME_PARAMS = {
  /**
   * Amount to darken/lighten the color for hover effect
   */
  hoverAmmount: 10,
  /**
   * Amount to darken/lighten the color for shaded effect
   */
  shadedAmmount: 5,
  /**
   * Amount to darken/lighten the color for support effect
   */
  supportAmmount: 5
};

/**
 * Values to generate the font face.
 */
const FONT_PARAMS = {
  hostingLocation: 'https://cdnstatic.usheru.com/fonts/',
  fontFamilyName: 'movie-font',
  normalWeight: '500',
  normalStyle: 'normal',
  boldWeight: '700',
  boldStyle: 'normal',
  attributeFont: 'data-font',
  attributeFontBold: 'data-font-bold'
};

@Injectable({
  providedIn: 'root'
})
export class MovieSiteConfigurationService {
  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly channelService = inject(ChannelService);
  private readonly metatagService = inject(MetatagsService);
  private readonly imageService = inject(ImageService);
  private readonly titleService = inject(TitleService);

  private readonly env: Environment = inject(ENVIRONMENT);
  private readonly document = inject(DOCUMENT);
  private readonly location = inject(Location);

  private movie: Movie | undefined = undefined;
  private currentType: 'film-info' | 'book-now' | undefined = undefined;

  readonly pageConfiguration = signal<Partial<MovieSitePageConfiguration>>(INITIAL_STATUS.pageConfiguration);
  readonly topMenuConfiguration = signal<Partial<MovieSiteTopMenuConfiguration>>(INITIAL_STATUS.topMenuConfiguration);
  readonly landingConfiguration = signal<Partial<MovieSiteLandingConfiguration>>(INITIAL_STATUS.landingConfiguration);
  readonly landingVideoModeConfiguration = computed(
    () => this.landingConfiguration() as MovieSiteLandingVideoConfiguration
  );
  readonly synopsisConfiguration = signal<Partial<MovieSiteSynopsisConfiguration>>(
    INITIAL_STATUS.synopsisConfiguration
  );

  readonly logosConfiguration = signal<Partial<MovieSiteLogosConfiguration>>(INITIAL_STATUS.logosConfiguration);

  readonly mainLogoTreatment = signal<{
    replacementUrl?: string;
    treatmentWhenContrastBlack?: null | 'drop-shadow' | string;
    treatmentWhenContrastWhite?: null | 'drop-shadow' | string;
  }>({});
  /**
   * TODO to implement footer configuration in the component
   */
  readonly footerConfiguration = signal<Partial<MovieSiteFooterConfiguration>>(INITIAL_STATUS.footerConfiguration);
  readonly customNavigationButtonConfiguration = signal<MovieSiteCustomNavigationButtonConfig | undefined>(undefined);

  readonly colorScheme = signal<MovieSiteColorSchemeConfiguration>(INITIAL_COLOR_SCHEME);
  readonly sideMenuColorScheme = computed<MovieSiteSideMenuColorSchemeConfiguration>(() => {
    const sideMenuColorScheme = this.topMenuConfiguration()?.sideMenu?.customSchemeColor;
    if (!sideMenuColorScheme) return undefined;
    const colorScheme = this.colorScheme();
    if (!colorScheme) return undefined;
    return this.generateSideMenuCustomColors(sideMenuColorScheme, colorScheme);
  });

  /**
   * This one includes the generated values.
   */
  readonly colorSchemeComputed = computed<MovieSiteColorSchemeConfiguration>(() =>
    this.generateColorScheme(this.colorScheme())
  );

  /**
   * List of contrast used in some element to determine
   * in what color fill a logo when it is use the drop-shadow trick.
   */
  readonly contrastMap = computed<Record<string, 'black' | 'white'>>(() => this.prepareContrastMap());

  readonly font = signal<string>(INITIAL_FONTS.font);
  readonly fontBold = signal<string>(INITIAL_FONTS.fontBold);

  readonly status = computed<Status>(() => ({
    pageConfiguration: this.pageConfiguration(),
    topMenuConfiguration: this.topMenuConfiguration(),
    landingConfiguration: this.landingConfiguration(),
    synopsisConfiguration: this.synopsisConfiguration(),
    theme: {
      colorScheme: this.colorSchemeComputed(),
      fonts: {
        font: this.font(),
        fontBold: this.fontBold()
      }
    },
    footerConfiguration: this.footerConfiguration()
  }));

  readonly footerChannelLogoTreatment = computed(() => {
    const footerParams = {} as MovieSiteFooterLogoParams;
    const chTreatment = this.mainLogoTreatment();
    const contrast =
      this.colorSchemeComputed().generatedValues?.backgroundColorContrast === '#ffffff' ? 'white' : 'black';
    const treatmentInUse =
      contrast === 'black' ? chTreatment?.treatmentWhenContrastBlack : chTreatment?.treatmentWhenContrastWhite;
    if (treatmentInUse === undefined) return {};
    if (treatmentInUse?.startsWith('http')) {
      footerParams.mainLogoAlternative = treatmentInUse;
    } else if (treatmentInUse === 'drop-shadow') {
      footerParams.dropShadow = contrast;
    }
    return footerParams;
  });

  readonly movieSiteConfigGlobal = signal<MovieSiteConfigGlobal>(this.env.movieSiteGlobalConfig || {});

  private readonly hasOverriedCSSBodybeenInserted = signal<HTMLStyleElement | undefined>(undefined);
  private readonly hasOverriedCSSbeenInserted = signal<HTMLStyleElement | undefined>(undefined);
  private readonly hasMovieFontBeenInserted = signal<HTMLStyleElement | undefined>(undefined);
  private readonly hasMovieFontBoldBeenInserted = signal<HTMLStyleElement | undefined>(undefined);

  private readonly appFooter = this.env?.movieSiteFooterComponent;

  /**
   * Value to store originally values of the status before be change with the live editor.
   */
  storedStatus: Partial<Status> = {};

  customScript: HTMLScriptElement | undefined;

  constructor() {
    // DEBUGGING - Effect log
    // effect(() => {
    //   console.info('MOVIE SITE STATUS', this.status());
    //   console.info('ENV CHANNEL GLOBAL CONFIG', this.movieSiteConfigGlobal());
    // });
    // Effect to insert the variables into the body
    effect(() => {
      this.updateBodyVariables();
    });

    // Effect to update the movie font
    effect(() => {
      this.updateFontInsertion({
        font: this.font(),
        element: this.hasMovieFontBeenInserted(),
        attribute: FONT_PARAMS.attributeFont,
        params: {
          fontFamilyName: FONT_PARAMS.fontFamilyName,
          hostingLocation: FONT_PARAMS.hostingLocation,
          weight: FONT_PARAMS.normalWeight,
          style: FONT_PARAMS.normalStyle
        }
      });
    });
    // Effect to update the movie font bold
    effect(() => {
      this.updateFontInsertion({
        font: this.fontBold(),
        element: this.hasMovieFontBoldBeenInserted(),
        attribute: FONT_PARAMS.attributeFontBold,
        params: {
          fontFamilyName: FONT_PARAMS.fontFamilyName,
          hostingLocation: FONT_PARAMS.hostingLocation,
          weight: FONT_PARAMS.boldWeight,
          style: FONT_PARAMS.boldStyle
        }
      });
    });
  }

  /**
   * This method must be called in the initialization of the movie site.
   * It prepares the service with the movie configuration.
   */
  prepareService(movie: Movie, type: 'film-info' | 'book-now') {
    if (movie.id === this.movie?.id && type === this.currentType) return;
    this.movie = movie;
    this.currentType = type;
    // GET ENVIRONMENT MOVIE CONFIGURATION
    const envMovieSiteConfigs = this.env?.movieSiteConfig || {};
    const envMovieConfig = envMovieSiteConfigs[movie.friendlyUrl] || {};
    this.customNavigationButtonConfiguration.set(envMovieConfig.customNavigationButton);
    const envGlobalConfig = this.movieSiteConfigGlobal();
    // GET MOVIE CONFIGURATION FROM BACKEND
    const backendStatus = this.adaptBackendConfiguration(movie);
    // INSERT REQUIRED CSS
    this.insertOverrideCSS();
    // SET THE STATUS CONFIGURATIONS
    const pageConfig = { ...envGlobalConfig.page, ...envMovieConfig.page };
    this.setPageConfiguration(
      this.pickConfiguration<MovieSitePageConfiguration>(
        backendStatus.pageConfiguration,
        pageConfig as MovieSitePageConfiguration
      )
    );

    const landingConfig = { ...envGlobalConfig.landingSection, ...envMovieConfig.landingSection };
    this.setLandingConfiguration(
      this.pickConfiguration<MovieSiteLandingConfiguration>(
        backendStatus.landingConfiguration,
        landingConfig as MovieSiteLandingConfiguration
      )
    );

    const synopsisConfiguration = { ...envMovieConfig.synopsis };
    this.setSynopsisConfiguration(
      this.pickConfiguration<MovieSiteSynopsisConfiguration>(
        { backgroundColor: backendStatus.synopsisConfiguration?.backgroundColor },
        synopsisConfiguration as MovieSiteSynopsisConfiguration
      )
    );

    const topMenuConfig = {
      ...envGlobalConfig?.topMenu,
      ...envMovieConfig.topMenu
    };

    this.setTopMenuConfiguration(
      this.pickConfiguration<MovieSiteTopMenuConfiguration>(
        backendStatus.topMenuConfiguration,
        topMenuConfig as MovieSiteTopMenuConfiguration
      )
    );

    const footerConfig = { ...envGlobalConfig?.footer, ...envMovieConfig.footer };
    this.setFooterConfiguration(
      this.pickConfiguration<MovieSiteFooterConfiguration>(
        backendStatus.footerConfiguration,
        footerConfig as MovieSiteFooterConfiguration
      )
    );

    // Not config in the env yet
    const backLogosConfig = backendStatus.logosConfiguration;
    this.setLogosConfiguration(this.pickConfiguration<MovieSiteLogosConfiguration>(backLogosConfig));

    // TREATMENT FOR CHANNEL LOGO
    const treatment = {};
    const chLogoBlackVersion = this.channelService.channel.properties?.connect?.logo?.darker;
    treatment['treatmentWhenContrastBlack'] = chLogoBlackVersion;
    const chLogoWhiteVersion = this.channelService.channel.properties?.connect?.logo?.lighter;
    treatment['treatmentWhenContrastWhite'] = chLogoWhiteVersion;
    const channelTreatmentGlobal = this.movieSiteConfigGlobal()?.mainLogoTreatment || treatment;
    const mainLogoTreatment = envMovieConfig.mainLogoTreatment || {};
    this.setChannelLogoTreatment({ ...mainLogoTreatment, ...channelTreatmentGlobal });

    // SET THEME CONFIGURATIONS
    this.insertFonts();
    this.setThemeConfiguration(this.pickConfiguration<MovieSiteTheme>(backendStatus.theme, envMovieConfig.theme));
    // END SET THEME CONFIGURATIONS
    // SET METATAGS
    this.setMetatags();
    this.setTabTitle({
      omitChannelTitleOnTab: true,
      channelName: this.channelService.channel.name
    });
    // Store the initial status as backup for the live editor resets
    this.storedStatus = JSON.parse(JSON.stringify(this.status()));
    // console.log('storedStatus', this.storedStatus);
    // Prepare the custom script if exists
    this.prepareCustomScript(this.movieSiteConfigGlobal()?.customScript);
  }

  prepareCustomScript(script: string) {
    if (!script || !this.isBrowser) return;
    const scriptElement = this.document.createElement('script');
    scriptElement.innerHTML = script;
    this.customScript = this.document.head.appendChild(scriptElement);
  }

  /**
   * Method to remove the movie configuration from the service.
   * This is used to reset the service when the movie changes or the user
   * leaves the page.
   * Is REQUIRED to use this in the ngOnDestroy of the component.
   */
  resetService() {
    this.movie = undefined;
    this.setLandingConfiguration(INITIAL_STATUS.landingConfiguration);
    this.setTopMenuConfiguration(INITIAL_STATUS.topMenuConfiguration);
    this.setFooterConfiguration(INITIAL_STATUS.footerConfiguration);
    this.customNavigationButtonConfiguration.set(undefined);
    this.setColorScheme(INITIAL_COLOR_SCHEME);
    this.setFont(INITIAL_FONTS.font);
    this.setFontBold(INITIAL_FONTS.fontBold);
    this.removeBodyClass();
    this.resetFontInsertion();
    this.storedStatus = {};
    // Remove the custom script if exists
    this.customScript?.remove();
    this.customScript = undefined;
    // There is no need to remove other css inserted
  }

  /**
   * Method used to pick configuration from the backend and environment.
   * It merges the backend configuration with the environment configuration.
   */
  private pickConfiguration<T>(backend: Partial<T> = {} as T, environment: T = {} as T): T {
    return {
      ...backend,
      ...environment
    };
  }

  /**
   * Parse the backend configuration and adapt it to the expected format.
   */
  private adaptBackendConfiguration(movie: Movie): Partial<Status> {
    /**
     * Parse values like "'[value, value]'" or "'value,value'" into an array.
     */
    function parseStringArray(value: string[] | string, fallback?: unknown[]) {
      if (!value) return fallback || null;
      return Array.isArray(value)
        ? value
        : value
            .replace(/^['"]|['"]$/g, '')
            .split(',')
            .map(item => item.trim());
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const properties = movie.properties as any;

    const {
      theme = {},
      logos = {},
      landing = {},
      topMenu = {},
      synopsis = {}
    } = (properties as MovieSiteConfigProperties) || {};

    const parsedValues: Partial<Status> = {
      theme: {
        colorScheme: {
          brandColor: theme?.mainColor,
          backgroundColor: theme?.backgroundColor,
          customBrandContrast: theme?.mainColorContrast,
          customBackgroundContrast: theme?.backgroundColorContrast
        },
        fonts: {
          font: theme?.fontFile,
          fontBold: theme?.boldFontFile
        }
      },
      logosConfiguration: {
        mainLogo: {
          disableInteraction: logos?.mainLogoDisableInteraction
        }
      },
      topMenuConfiguration: {
        mainLogo: {
          desktop: topMenu?.mainLogo?.desktop
        },
        backgroundSmallScreen: topMenu?.backgroundSmallScreen
          ? new TinyColor(topMenu.backgroundSmallScreen).toHexString()
          : undefined,
        burgerBackground: topMenu?.burgerBackground
          ? new TinyColor(topMenu.burgerBackground).toHexString()
          : undefined,
        preventCalendar: topMenu?.preventCalendar,
        sideMenu: {
          // logosColorMode: topMenu?.sideMenu?.logosColorMode,
          customSchemeColor: topMenu?.sideMenu?.customSchemeColor
        }
      },
      landingConfiguration: {
        display: landing?.display,
        shortVideoFile: landing?.shortVideoFile,
        logoPosition: {
          mainPosition: parseStringArray(
            landing?.logoPositionMainPosition,
            !landing?.display || landing.display === 'image'
              ? INITIAL_STATUS.landingConfiguration.logoPosition.mainPosition
              : INITIAL_LANDING_VIDEO.logoPosition.mainPosition
          ) as [HorizontalPosition, VerticalPosition],
          offset: parseStringArray(
            landing?.logoPositionOffset,
            INITIAL_STATUS.landingConfiguration.logoPosition.offset
          )?.map((v, i) => (i == 0 || i == 2 ? Number(v) : (v as string))) as [number, LengthUnits, number, LengthUnits]
        },
        hideReleaseMessage: landing?.hideReleaseMessage,
        releaseMessage: {
          maxWidth: landing?.releaseMessageMaxWidth || INITIAL_STATUS.landingConfiguration.releaseMessage?.maxWidth
        },
        releaseMessagePosition: {
          horizontal:
            landing?.releaseMessagePositionHorizontal ||
            INITIAL_STATUS.landingConfiguration.releaseMessagePosition?.horizontal
        },
        gradientBottom: landing?.hideGradient != null ? !landing.hideGradient : true,
        gradientBottomMobile:
          landing?.hideGradientMobile != null
            ? landing.hideGradientMobile === 'inherit'
              ? 'inherit'
              : !landing.hideGradientMobile
            : 'inherit',
        gradientIntensity: landing?.gradientIntensity || INITIAL_STATUS.landingConfiguration.gradientIntensity,
        mediaFit:
          landing?.mediaFit || (INITIAL_STATUS.landingConfiguration as MovieSiteLandingCoverConfiguration)?.mediaFit,
        mediaPosition:
          landing?.mediaPosition ||
          (INITIAL_STATUS.landingConfiguration as MovieSiteLandingCoverConfiguration)?.mediaPosition
      },
      synopsisConfiguration: {
        backgroundColor: synopsis?.backgroundColor
          ? new TinyColor(synopsis.backgroundColor).toHexString()
          : INITIAL_STATUS.synopsisConfiguration.backgroundColor,
        headerText: synopsis?.headerText || INITIAL_STATUS.synopsisConfiguration.headerText,
        bodyTextColor:
          new TinyColor(synopsis?.bodyTextColor)?.toHexString() || INITIAL_STATUS.synopsisConfiguration?.bodyTextColor
      }
    };

    return parsedValues;
  }

  // THEME CONFIGURATION ///////////////////////////////////////////////////////////////////////////////////////

  private setThemeConfiguration(args: MovieSiteTheme) {
    const { colorScheme, fonts } = args;
    this.setColorScheme(colorScheme);
    this.setFont(fonts.font || INITIAL_FONTS.font);
    this.setFontBold(fonts.fontBold || INITIAL_FONTS.fontBold);
  }

  /**
   * Fills the generated values in the color scheme and fix the base colors.
   * This is used to ensure that the color scheme is always valid and has the required values
   */
  private generateColorScheme(colorScheme: MovieSiteColorSchemeConfiguration): MovieSiteColorSchemeConfiguration {
    const colorBlack = new TinyColor('#000000');
    const colorWhite = new TinyColor('#FFFFFF');

    let customBrandContrast, customBackgroundContrast;
    if (colorScheme?.customBrandContrast) customBrandContrast = new TinyColor(colorScheme.customBrandContrast);
    if (colorScheme?.customBackgroundContrast)
      customBackgroundContrast = new TinyColor(colorScheme.customBackgroundContrast);

    function generateColorContrast(color: TinyColor): string {
      return (isReadable(color, colorBlack) ? colorBlack : colorWhite).toHexString();
    }

    function generateColorShade(color: TinyColor, ammount: number): string {
      return (color.isLight() ? color.lighten(ammount) : color.darken(ammount)).toHexString();
    }

    function generateColorSupport(color: TinyColor, ammount: number): string {
      return (isReadable(color, colorBlack) ? colorBlack.lighten(ammount) : colorWhite.darken(ammount)).toHexString();
    }

    const brandColor = new TinyColor(colorScheme.brandColor);
    const backgroundColor = new TinyColor(colorScheme.backgroundColor);

    const brandColorSupport = generateColorSupport(brandColor, THEME_PARAMS.supportAmmount);
    const backgroundColorSupport = generateColorSupport(backgroundColor, THEME_PARAMS.supportAmmount);

    // This is to transform values like ffffff to #ffffff if they are not already in hex format
    colorScheme.brandColor = new TinyColor(brandColor).toHexString();
    colorScheme.backgroundColor = new TinyColor(backgroundColor).toHexString();

    colorScheme.generatedValues = {
      brandColorContrast: customBrandContrast || generateColorContrast(brandColor),
      brandColorHover: generateColorShade(brandColor, THEME_PARAMS.hoverAmmount),
      brandColorShaded: generateColorShade(brandColor, THEME_PARAMS.shadedAmmount),
      brandColorSupport: brandColorSupport,
      brandColorSupportContrast: generateColorContrast(new TinyColor(brandColorSupport)),
      //
      backgroundColorContrast: customBackgroundContrast || generateColorContrast(backgroundColor),
      backgroundColorShaded: generateColorShade(backgroundColor, THEME_PARAMS.shadedAmmount),
      backgroundColorSupport: backgroundColorSupport,
      backgroundColorSupportContrast: generateColorContrast(new TinyColor(backgroundColorSupport))
    };

    // EXTRA VALUES
    colorScheme.extraValues = {};
    colorScheme.extraValues.brandColorContrast = new TinyColor(brandColor).isLight() ? 'black' : 'white';
    colorScheme.extraValues.backgroundContrast = backgroundColor.isLight() ? 'black' : 'white';
    colorScheme.extraValues.brandColorSupportContrast = new TinyColor(brandColorSupport).isLight() ? 'black' : 'white';
    colorScheme.extraValues.backgroundColorSupportContrast = new TinyColor(backgroundColorSupport).isLight()
      ? 'black'
      : 'white';

    /**
     * TODO find a way to separate the border radius from the color scheme.
     */
    colorScheme.borderRadius = colorScheme.borderRadius || INITIAL_COLOR_SCHEME.borderRadius;

    return colorScheme;
  }

  /**
   * Generates the CSS to bind to the style attribute of the movie-site component.
   */
  public getCSSStyleFromTheme(): Record<string, string> {
    const styles: Record<string, string> = {};
    const colors = this.colorSchemeComputed();
    const regex = /([a-z])([A-Z])/g;
    const kebabCase = (str: string) => str.replace(regex, '$1-$2').toLowerCase();
    for (const key in colors) {
      // skip keys that start with 'font' as they are not CSS variables
      if (key.indexOf('font') !== 0) {
        // replace cammelCase with kebab-case
        const cssKey = kebabCase(key).toLowerCase();
        if (key !== 'generatedValues') {
          styles[`--${cssKey}`] = colors[key as keyof MovieSiteColorSchemeConfiguration] as string;
        } else if (colors.generatedValues) {
          for (const generatedKey in colors.generatedValues) {
            const generatedCssKey = kebabCase(generatedKey);
            styles[`--${generatedCssKey}`] = colors.generatedValues[
              generatedKey as keyof MovieSiteColorSchemeConfiguration['generatedValues']
            ] as string;
          }
        }
      }
    }
    return styles;
  }

  /**
   * Inserts the fonts into the document head.
   */
  private insertFonts(): void {
    if (this.hasMovieFontBeenInserted() === undefined) {
      const font = this.font();
      const fontStyle = this.document.createElement('style');
      const fontUrl = `${FONT_PARAMS.hostingLocation}${font}`;
      fontStyle.setAttribute(FONT_PARAMS.attributeFont, font);
      fontStyle.innerHTML = generateFontFace({
        fontFamily: FONT_PARAMS.fontFamilyName,
        fontUrl: fontUrl,
        fontWeight: FONT_PARAMS.normalWeight,
        fontStyle: FONT_PARAMS.normalStyle
      });

      this.document.head.appendChild(fontStyle);
      this.hasMovieFontBeenInserted.set(fontStyle);
    }
    //////////////////////////////
    if (this.hasMovieFontBoldBeenInserted() === undefined) {
      const fontBold = this.fontBold();
      const fontBoldStyle = this.document.createElement('style');
      const fontBoldUrl = `${FONT_PARAMS.hostingLocation}${fontBold}`;
      fontBoldStyle.setAttribute(FONT_PARAMS.attributeFontBold, fontBold);
      fontBoldStyle.innerHTML = generateFontFace({
        fontFamily: FONT_PARAMS.fontFamilyName,
        fontUrl: fontBoldUrl,
        fontWeight: FONT_PARAMS.boldWeight,
        fontStyle: FONT_PARAMS.boldStyle
      });

      this.document.head.appendChild(fontBoldStyle);
      this.hasMovieFontBoldBeenInserted.set(fontBoldStyle);
    }
    //////////////////////////////
  }

  /**
   * Updates the current inserted font in the document head based on the current theme.
   */
  private updateFontInsertion(args: {
    font: string;
    element: HTMLStyleElement;
    attribute?: string;
    params: {
      fontFamilyName: string;
      hostingLocation: string;
      weight: string;
      style: string;
    };
  }) {
    const { font, element, attribute, params } = args;

    // If there is not element to update yet, do nothing.
    if (!element) return;

    element.setAttribute(attribute, font);
    element.innerHTML = generateFontFace({
      fontFamily: params.fontFamilyName || FONT_PARAMS.fontFamilyName,
      fontUrl: `${params.hostingLocation}${font}`,
      fontWeight: params.weight,
      fontStyle: params.style
    });
  }

  private resetFontInsertion() {
    const styleFont = this.hasMovieFontBeenInserted();
    const styleFontBold = this.hasMovieFontBoldBeenInserted();
    if (styleFont) styleFont.remove();
    if (styleFontBold) styleFontBold.remove();
    this.hasMovieFontBeenInserted.set(undefined);
    this.hasMovieFontBoldBeenInserted.set(undefined);
  }

  private insertBodyClass() {
    this.document.body.classList.add(BODY_CLASS_NAME);
  }

  private removeBodyClass() {
    this.document.body.classList.remove(BODY_CLASS_NAME);
  }

  /**
   * Injects the override CSS into the document head.
   * This is used to ensure that the movie-site component can be used in any context
   * without being affected by the styles of the surrounding components or the
   * app itself.
   * This style is static and there is no need to be removed or updated,
   * only inserted once.
   */
  private insertOverrideCSS(): void {
    this.insertBodyClass();
    this.inserOverrideBodyCSS();
    if (this.hasOverriedCSSbeenInserted()) return;
    const style = this.document.createElement('style');
    style.setAttribute('data-movie-site-override-css', 'true');
    style.innerHTML = MOVIE_SITE_OVERRIDE_CSS;
    this.document.head.appendChild(style);
    this.hasOverriedCSSbeenInserted.set(style);
  }

  /**
   * Injects the override CSS into the document head.
   * This will be foucus in add style variables to the body element
   * to be use with the styles inserted by the override CSS.
   */
  private inserOverrideBodyCSS(): void {
    if (!this.hasOverriedCSSBodybeenInserted()) {
      const style = this.document.createElement('style');
      style.setAttribute('data-movie-site-override-css', 'true');
      this.document.head.appendChild(style);
      this.hasOverriedCSSBodybeenInserted.set(style);
    }
  }

  /**
   * Inserts a stlye element to add to the body the color scheme variables
   * to the body. This is to style element
   */
  private updateBodyVariables() {
    if (!this.hasOverriedCSSBodybeenInserted()) return;
    const style = this.hasOverriedCSSBodybeenInserted();
    let innnerHTML = 'body {\n';
    for (const [key, value] of Object.entries(this.getCSSStyleFromTheme())) {
      innnerHTML += `  ${key.replace('--', '--movie-site_')}: ${value};\n`;
    }
    for (const [key, value] of Object.entries(this.getMovieSiteBodyCustomVariables())) {
      innnerHTML += `  ${key}: ${value};\n`;
    }
    innnerHTML += '}';
    style.innerHTML = innnerHTML;
  }

  private getMovieSiteBodyCustomVariables(): Record<string, string> {
    if (!this.movie) return {};

    const subscribeFormBackgroundColor = getChannelMovieProp(
      'movieSite.footer.subscribeFormBackgroundColor',
      this.channelService.channel,
      this.movie
    );

    if (!subscribeFormBackgroundColor) return {};

    const subscribeFormBackgroundColorValue = new TinyColor(subscribeFormBackgroundColor);
    if (!subscribeFormBackgroundColorValue.isValid) return {};

    const contrastColor = isReadable(subscribeFormBackgroundColorValue, '#000000') ? '#000000' : '#ffffff';

    return {
      '--movie-site_footer-subscribe-form-background-color': subscribeFormBackgroundColorValue.toHexString(),
      '--movie-site_footer-subscribe-form-background-color-contrast': contrastColor
    };
  }

  // END THEME CONFIGURATION ///////////////////////////////////////////////////////////////////////////////////

  // METADATA CONFIGURATION ///////////////////////////////////////////////////////////////////////////////////

  setMetatags(): void {
    const movie = this.movie;
    if (!movie) {
      console.error('No Movie setting metatags');
      return;
    }

    const metaTags: MetaTags = {
      url: this.location.path(),
      title: $localize`:@@meta.page.movie-detail.title:_channel_ - _movie_`,
      favicon: movie?.faviconImageUrl,
      description: $localize`:@@meta.page.movie-detail.description:Movie detail`,
      video: movie.trTrailerLink
    };

    if (movie.tilePhotoUrl) {
      metaTags.image = this.imageService.computeUrl(movie?.tilePhotoUrl || '', { width: 1280 });
    } else if (movie.portraitPhotoUrl) {
      metaTags.image = this.imageService.computeUrl(movie?.portraitPhotoUrl || '', { height: 1000 });
    }

    const director = movie?.movieCrew?.find(mc => (mc?.crewRole?.name || '').toLowerCase() == 'director')?.star?.name;

    this.metatagService.setMetaTags(metaTags, {
      movie: movie.title,
      director: director
    });
  }

  setTabTitle(args: { omitChannelTitleOnTab?: boolean; channelName?: string } = {}) {
    const { omitChannelTitleOnTab, channelName } = args;
    const movie = this.movie;
    if (movie?.title) {
      const tabTitle = omitChannelTitleOnTab || !channelName ? movie.title : `${channelName} - ${movie.title}`;
      this.titleService.setTitle({
        title: tabTitle,
        avoidDefaultConversion: true
      });
    }
  }

  // END METADATA CONFIGURATION ////////////////////////////////////////////////////////////////////////////////

  // SETTERS ///////////////////////////////////////////////////////////////////////////////////////

  public setPageConfiguration(config: Partial<MovieSitePageConfiguration> = {}) {
    config = { ...INITIAL_STATUS.pageConfiguration, ...config };
    this.pageConfiguration.set(config);
  }

  public setTopMenuConfiguration(config: Partial<MovieSiteTopMenuConfiguration> = {}) {
    config = { ...INITIAL_STATUS.topMenuConfiguration, ...config };

    // This is to convert the customSchemeColor from number to string
    try {
      const sideMenuCustomColor = Number.parseInt(config.sideMenu?.customSchemeColor || '', 10);
      if (sideMenuCustomColor && !isNaN(sideMenuCustomColor)) {
        switch (sideMenuCustomColor) {
          case 1:
            config.sideMenu.customSchemeColor = 'brand-color-support';
            break;
          case 2:
            config.sideMenu.customSchemeColor = 'brand-color';
            break;
          case 3:
            config.sideMenu.customSchemeColor = 'background-color';
            break;
          case 4:
            config.sideMenu.customSchemeColor = 'background-color-support';
            break;
          default:
            config.sideMenu.customSchemeColor = undefined;
            break;
        }
      }
    } finally {
    }

    this.topMenuConfiguration.set(config);
  }

  public setLandingConfiguration(config: Partial<MovieSiteLandingConfiguration> = {}) {
    if (!config.display) config.display = 'image';
    config = this.discardUndefined<Partial<MovieSiteLandingConfiguration>>(config);
    if (config.display === 'image') {
      config = { ...INITIAL_STATUS.landingConfiguration, ...config };
    } else {
      config = { ...INITIAL_STATUS.landingConfiguration, ...INITIAL_LANDING_VIDEO, ...config };
    }
    this.landingConfiguration.set(config);
  }

  public setSynopsisConfiguration(config: Partial<MovieSiteSynopsisConfiguration> = {}) {
    config = { ...INITIAL_STATUS.synopsisConfiguration, ...config };
    try {
      if (config.backgroundColor) config.backgroundColor = new TinyColor(config.backgroundColor).toHexString();
      if (config.bodyTextColor) config.bodyTextColor = new TinyColor(config.bodyTextColor).toHexString();
    } catch (error) {
      console.error('Error processing bodyTextColor:', error);
    }
    this.synopsisConfiguration.set(config);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public setFooterConfiguration(config: Partial<MovieSiteFooterConfiguration> = {}) {
    const appFooter = this.appFooter;
    if (config.component) {
      config = { ...INITIAL_STATUS.footerConfiguration, ...config };
    } else {
      config = {
        ...INITIAL_STATUS.footerConfiguration,
        ...{ component: appFooter || DEFAULT_FOOTER_COMPONENT },
        ...config
      };
    }
    this.footerConfiguration.set(config);
  }

  public setLogosConfiguration(config: Partial<MovieSiteLogosConfiguration> = {}) {
    config = { ...INITIAL_STATUS.logosConfiguration, ...config };
    this.logosConfiguration.set(config);
  }

  public setChannelLogoTreatment(treatment: {
    treatmentWhenContrastBlack?: null | 'drop-shadow' | string;
    treatmentWhenContrastWhite?: null | 'drop-shadow' | string;
  }) {
    this.mainLogoTreatment.set(treatment);
  }

  public setColorScheme(colorScheme: MovieSiteColorSchemeConfiguration): void {
    colorScheme = { ...INITIAL_COLOR_SCHEME, ...colorScheme };
    this.colorScheme.set(colorScheme);
  }

  public setFont(font: string) {
    this.font.set(font || INITIAL_FONTS.font);
  }

  public setFontBold(fontBold: string) {
    this.fontBold.set(fontBold || INITIAL_FONTS.fontBold);
  }

  effectMetaTitleFromMovieTranslations = ({ movie, translations, hasLoaded }: Args) => {
    let lastMetaTitle: string | undefined;

    return effect(() => {
      if (!movie()) return;
      if (!hasLoaded()) return;
      const translatedTitle = translations()?.['meta.page.movie-detail.title'];
      if (!translatedTitle) return;
      if (lastMetaTitle === translatedTitle) return;
      lastMetaTitle = translatedTitle;
      this.updateMetaTitleFromMovieTranslations(translatedTitle);
    });
  };

  private updateMetaTitleFromMovieTranslations(translatedTitle?: string): void {
    const movie = this.movie;
    if (!movie) {
      return;
    }

    if (!translatedTitle) {
      return;
    }

    this.metatagService.updateTitle({
      title: translatedTitle,
      ...movie
    });
  }

  /**
   * Checks **contrastMap** for more details.
   * This is used to determine the contrast color to use in some elements.
   */
  private prepareContrastMap() {
    const colorScheme = this.colorSchemeComputed();

    const customTopMenuLargeBg = this.topMenuConfiguration().backgroundLargeScreen;
    let customTopMenuLargeContrast: 'black' | 'white' = this.topMenuConfiguration().backgroundLargeScreenContrast;
    // remember that exist backgroundLargeScreenContrast in the configuration
    if (!customTopMenuLargeContrast && customTopMenuLargeBg && !customTopMenuLargeBg.startsWith('var(--')) {
      const color = new TinyColor(customTopMenuLargeBg);
      customTopMenuLargeContrast = isReadable(color, new TinyColor('#000000')) ? 'black' : 'white';
    }
    //
    const mainLogoConfig = this.topMenuConfiguration().mainLogo?.desktop;
    const mainLogoContrast = mainLogoConfig === 'light' ? 'white' : mainLogoConfig === 'dark' ? 'black' : undefined;

    const customTopMenuSmallBg = this.topMenuConfiguration().backgroundSmallScreen;
    let customTopMenuSmallContrast: 'black' | 'white' =
      this.topMenuConfiguration().backgroundSmallScreenContrast || null;
    // remember that exist backgroundSmallScreenContrast in the configuration
    // for variable values
    if (!customTopMenuSmallContrast && customTopMenuSmallBg && !customTopMenuSmallBg.startsWith('var(--')) {
      const color = new TinyColor(customTopMenuSmallBg);
      customTopMenuSmallContrast = isReadable(color, new TinyColor('#000000')) ? 'black' : 'white';
    }

    const customBurgerBg = this.topMenuConfiguration().burgerBackground;
    let customBurgerContrast: 'black' | 'white' = this.topMenuConfiguration().burgerBackgroundContrast || null;
    // remember that exist burgerBackgroundContrast in the configuration
    // for variable values
    if (!customBurgerContrast && customBurgerBg && !customBurgerBg.startsWith('var(--')) {
      const color = new TinyColor(customBurgerBg);
      customBurgerContrast = isReadable(color, new TinyColor('#000000')) ? 'black' : 'white';
    }

    return {
      topMenuLarge:
        customTopMenuLargeContrast ||
        mainLogoContrast ||
        this.landingConfiguration().contrastForLogos ||
        (colorScheme.extraValues?.backgroundContrast === 'white' ? 'white' : 'black'),

      topMenuSmall:
        customTopMenuSmallContrast ||
        (colorScheme.generatedValues?.backgroundColorContrast === '#ffffff' ? 'white' : 'black'),
      sideMenu: colorScheme.generatedValues?.brandColorSupportContrast === '#ffffff' ? 'white' : 'black',
      burger: customBurgerContrast
    } as Record<string, 'black' | 'white'>;
  }

  /**
   * Used for footer channel logo
   */
  public generateFooterChannelLogoTreatment(contrast: 'black' | 'white' | null): MovieSiteFooterLogoParams | null {
    const channelTreatment = this.mainLogoTreatment();
    if (!channelTreatment || !contrast) return null;
    const logoParams = {} as MovieSiteFooterLogoParams;
    if (channelTreatment.treatmentWhenContrastBlack && contrast === 'black') {
      if (channelTreatment.treatmentWhenContrastBlack === 'drop-shadow') {
        logoParams.dropShadow = 'black';
      } else {
        logoParams.mainLogoAlternative = channelTreatment.treatmentWhenContrastBlack;
      }
    } else if (channelTreatment.treatmentWhenContrastWhite && contrast === 'white') {
      if (channelTreatment.treatmentWhenContrastWhite === 'drop-shadow') {
        logoParams.dropShadow = 'white';
      } else {
        logoParams.mainLogoAlternative = channelTreatment.treatmentWhenContrastWhite;
      }
      return logoParams;
    }
    return null;
  }

  public generateSideMenuCustomColors(
    shceme: MovieProperties['topMenu']['sideMenu']['customSchemeColor'],
    colors: MovieSiteColorSchemeConfiguration
  ): MovieSiteSideMenuColorSchemeConfiguration {
    function generateHoverBackground(color: string): string {
      const tinyColor = new TinyColor(color);
      const hoverBackground = tinyColor.isDark()
        ? tinyColor.lighten(THEME_PARAMS.hoverAmmount)
        : tinyColor.darken(THEME_PARAMS.hoverAmmount);
      return hoverBackground.toHexString();
    }

    switch (shceme) {
      case 'brand-color': {
        return {
          background: colors.brandColor,
          text: colors.generatedValues?.brandColorContrast,
          buttonBackground: colors.generatedValues?.brandColorSupport,
          buttonText: colors.generatedValues?.brandColorSupportContrast,
          buttonHoverBackground: generateHoverBackground(colors.generatedValues?.brandColorSupport),
          logoContrast: colors.extraValues?.brandColorContrast
        };
      }
      case 'background-color': {
        return {
          background: colors.backgroundColor,
          text: colors.generatedValues?.backgroundColorContrast,
          buttonBackground: colors.generatedValues?.backgroundColorSupport,
          buttonText: colors.generatedValues?.backgroundColorSupportContrast,
          buttonHoverBackground: generateHoverBackground(colors.generatedValues?.backgroundColorSupport),
          logoContrast: colors.extraValues?.backgroundContrast
        };
      }
      case 'brand-color-support': {
        return {
          background: colors.generatedValues?.brandColorSupport,
          text: colors.generatedValues?.brandColorSupportContrast,
          buttonBackground: colors.brandColor,
          buttonText: colors.generatedValues?.brandColorContrast,
          buttonHoverBackground: colors.generatedValues?.brandColorShaded,
          logoContrast: colors?.extraValues?.brandColorSupportContrast
        };
      }
      case 'background-color-support': {
        return {
          background: colors.generatedValues?.backgroundColorSupport,
          text: colors.generatedValues?.backgroundColorSupportContrast,
          buttonBackground: colors.backgroundColor,
          buttonText: colors.generatedValues?.backgroundColorContrast,
          buttonHoverBackground: colors.generatedValues?.backgroundColorShaded,
          logoContrast: colors?.extraValues?.backgroundColorSupportContrast
        };
      }
    }
  }

  /**
   * Utility method to discard undefined values from an object.
   */
  private discardUndefined<T>(obj: unknown): T {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v !== undefined)) as T;
  }
}

type Args = {
  movie: Signal<Movie | undefined>;
  translations: Signal<MovieTranslations | undefined>;
  hasLoaded: Signal<boolean>;
};
